<template>
  <div class="sf-el photo-drawing-element">
    <div class="sf-photo-drawing">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>
      <p v-if="uploadText" class="sf-photo-drawing__instruction">{{ uploadText }}</p>

      <!-- Saved photos preview (multiple mode) -->
      <div
        v-if="multiple && savedPhotos.length"
        class="sf-photo-drawing__gallery"
      >
        <div
          v-for="(photo, idx) in savedPhotos"
          :key="photo.url || idx"
          class="sf-photo-drawing__gallery-item"
        >
          <img :src="photo.url" :alt="`Foto ${idx + 1}`" />
          <button
            type="button"
            class="sf-photo-drawing__gallery-remove"
            @click="removeSaved(idx)"
            aria-label="Verwijderen"
          >
            <BaseIcon name="xmark" size="12" />
          </button>
        </div>
      </div>

      <!-- Saved single photo preview (single mode) -->
      <div
        v-else-if="!multiple && savedPhoto"
        class="sf-photo-drawing__single-preview"
      >
        <img :src="savedPhoto.url" alt="Geannoteerde foto" />
        <div class="sf-photo-drawing__single-actions">
          <button
            type="button"
            class="sf-photo-drawing__btn"
            @click="resetCurrent"
          >
            <BaseIcon name="edit" size="14" />
            Opnieuw
          </button>
          <button
            type="button"
            class="sf-photo-drawing__btn sf-photo-drawing__btn--danger"
            @click="removeSaved(0)"
          >
            <BaseIcon name="trash" size="14" />
            Verwijderen
          </button>
        </div>
      </div>

      <!-- Step 1: Single dropzone CTA — opens native OS picker (camera / gallery / files) -->
      <div
        v-if="step === 'choose' && (multiple || !savedPhoto)"
        class="sf-photo-drawing__dropzone"
        @click="openFilePicker"
      >
        <div class="sf-photo-drawing__dropzone-icon">
          <BaseIcon name="image" size="20" />
        </div>
        <p class="sf-photo-drawing__dropzone-text">
          {{ multiple && savedPhotos.length > 0 ? 'Nog een foto toevoegen' : 'Foto toevoegen' }}
        </p>
        <p class="sf-photo-drawing__dropzone-hint">
          Tekenen en annoteren na het kiezen
        </p>
      </div>

      <!-- Step 2: Drawing canvas (teleported to body when fullscreen) -->
      <Teleport to="body" :disabled="!isFullscreen">
        <div
          v-if="step === 'edit'"
          class="sf-photo-drawing__editor"
          :class="{ 'sf-photo-drawing__editor--fullscreen': isFullscreen }"
        >
          <div class="sf-photo-drawing__toolbar">
            <div class="sf-photo-drawing__tool-toggle" role="group" aria-label="Gereedschap">
              <button
                type="button"
                class="sf-photo-drawing__tool-btn"
                :class="{ 'sf-photo-drawing__tool-btn--active': tool === 'pen' }"
                @click="selectTool('pen')"
                title="Tekenen"
              >
                <BaseIcon name="edit" size="14" />
                <span>Pen</span>
              </button>
              <button
                type="button"
                class="sf-photo-drawing__tool-btn"
                :class="{ 'sf-photo-drawing__tool-btn--active': tool === 'text' }"
                @click="selectTool('text')"
                title="Tekst plaatsen"
              >
                <span class="sf-photo-drawing__tool-letter">T</span>
                <span>Tekst</span>
              </button>
            </div>
            <div class="sf-photo-drawing__colors">
              <button
                v-for="color in availableColors"
                :key="color"
                type="button"
                class="sf-photo-drawing__color"
                :class="{ 'sf-photo-drawing__color--active': currentColor === color }"
                :style="{ backgroundColor: color }"
                @click="selectColor(color)"
                :aria-label="`Kleur ${color}`"
              />
            </div>
            <div class="sf-photo-drawing__brush">
              <label for="brush-size" class="sf-photo-drawing__brush-label">
                {{ tool === 'text' ? 'Maat' : 'Pen' }}
              </label>
              <input
                id="brush-size"
                type="range"
                min="2"
                max="20"
                v-model.number="brushSize"
                class="sf-photo-drawing__brush-range"
              />
            </div>
            <div class="sf-photo-drawing__tools">
              <button
                type="button"
                class="sf-photo-drawing__btn sf-photo-drawing__btn--icon"
                @click="resetView"
                :disabled="zoom === 1 && panX === 0 && panY === 0"
                :title="`Zoom ${Math.round(zoom * 100)}% — reset`"
              >
                <span class="sf-photo-drawing__zoom-label">{{ Math.round(zoom * 100) }}%</span>
              </button>
              <button
                type="button"
                class="sf-photo-drawing__btn sf-photo-drawing__btn--icon"
                @click="toggleFullscreen"
                :title="isFullscreen ? 'Sluiten' : 'Volledig scherm'"
              >
                <BaseIcon :name="isFullscreen ? 'xmark' : 'magnify'" size="16" />
              </button>
              <button
                type="button"
                class="sf-photo-drawing__btn sf-photo-drawing__btn--icon"
                @click="undo"
                :disabled="!canUndo"
                title="Ongedaan maken"
              >
                <BaseIcon name="redo" size="16" style="transform: scaleX(-1);" />
              </button>
              <button
                type="button"
                class="sf-photo-drawing__btn sf-photo-drawing__btn--icon"
                @click="clearStrokes"
                :disabled="strokes.length === 0"
                title="Alles wissen"
              >
                <BaseIcon name="trash" size="16" />
              </button>
            </div>
          </div>

          <div ref="canvasWrapper" class="sf-photo-drawing__canvas-wrapper">
            <canvas
              ref="canvas"
              class="sf-photo-drawing__canvas"
              :class="{ 'sf-photo-drawing__canvas--text-mode': tool === 'text' }"
              :style="canvasTransformStyle"
              @pointerdown="onPointerDown"
              @pointermove="onPointerMove"
              @pointerup="onPointerUp"
              @pointercancel="onPointerUp"
            />
            <div v-if="gestureMode === 'gesture'" class="sf-photo-drawing__gesture-hint">
              <BaseIcon name="information-circle" size="14" />
              Pinch om te zoomen, 2 vingers om te schuiven
            </div>
            <div
              v-else-if="tool === 'text' && !textEditor.open && !strokes.some((s) => s.type === 'text')"
              class="sf-photo-drawing__gesture-hint"
            >
              <BaseIcon name="information-circle" size="14" />
              Tik om tekst te plaatsen
            </div>
          </div>

          <!-- Text editor overlay (bottom sheet inside the editor wrapper) -->
          <div v-if="textEditor.open" class="sf-photo-drawing__text-editor">
            <div class="sf-photo-drawing__text-editor-bar">
              <span class="sf-photo-drawing__text-editor-title">
                {{ textEditor.mode === 'edit' ? 'Tekst bewerken' : 'Tekst toevoegen' }}
              </span>
              <input
                ref="textInputRef"
                v-model="textEditor.value"
                type="text"
                class="sf-photo-drawing__text-editor-input"
                :style="{ color: currentColor }"
                placeholder="Typ hier..."
                @keydown.enter.prevent="commitTextEditor"
                @keydown.esc.prevent="cancelTextEditor"
              />
              <div class="sf-photo-drawing__text-editor-actions">
                <button
                  type="button"
                  class="sf-photo-drawing__btn sf-photo-drawing__btn--icon"
                  @click="cancelTextEditor"
                  title="Annuleren"
                  aria-label="Annuleren"
                >
                  <BaseIcon name="xmark" size="14" />
                </button>
                <button
                  v-if="textEditor.mode === 'edit'"
                  type="button"
                  class="sf-photo-drawing__btn sf-photo-drawing__btn--icon sf-photo-drawing__btn--danger"
                  @click="deleteTextEditor"
                  title="Verwijderen"
                  aria-label="Verwijderen"
                >
                  <BaseIcon name="trash" size="14" />
                </button>
                <button
                  type="button"
                  class="sf-photo-drawing__btn sf-photo-drawing__btn--primary"
                  @click="commitTextEditor"
                  :disabled="!textEditor.value.trim()"
                >
                  <BaseIcon name="check" size="14" />
                  Klaar
                </button>
              </div>
            </div>
          </div>

          <div class="sf-photo-drawing__editor-actions">
            <button
              type="button"
              class="sf-photo-drawing__btn"
              @click="cancelEdit"
              :disabled="isUploading"
            >
              <BaseIcon name="xmark" size="14" />
              Annuleren
            </button>
            <button
              type="button"
              class="sf-photo-drawing__btn sf-photo-drawing__btn--primary"
              @click="saveDrawing"
              :disabled="isUploading"
            >
              <span v-if="isUploading" class="sf-photo-drawing__spinner" />
              <BaseIcon v-else name="check" size="14" />
              {{ isUploading ? 'Opslaan...' : 'Opslaan' }}
            </button>
          </div>
        </div>
      </Teleport>

      <!-- Error -->
      <div v-if="uploadError" class="sf-photo-drawing__error">
        <BaseIcon name="exclamation-circle" size="16" />
        {{ uploadError }}
      </div>

      <!-- Hidden file input: native OS picker handles camera vs gallery selection -->
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        @change="onSourcePicked"
        style="display: none"
      />
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, inject, onMounted, onBeforeUnmount, nextTick } from 'vue';
  import BaseIcon from '../../BaseIcon.vue';

  const getFileUploadService = async () => {
    const { fileUploadService } = await import('@shared/services/fileUploadService');
    return fileUploadService;
  };

  const DEFAULT_COLORS = [
    '#ef4444', // red
    '#f59e0b', // amber
    '#10b981', // emerald
    '#3b82f6', // blue
    '#000000', // black
    '#ffffff', // white
  ];

  // Limit max canvas size so we keep upload modest while preserving readability
  const MAX_CANVAS_DIMENSION = 1600;

  // Zoom limits for pinch-to-zoom
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;

  export default {
    name: 'PhotoDrawingElement',

    components: { BaseIcon },

    props: {
      label: String,
      caption: String,
      name: String,
      // Single mode: String (URL). Multiple mode: Array<String>.
      modelValue: {
        type: [String, Array],
        default: '',
      },
      required: Boolean,
      multiple: { type: Boolean, default: false },
      fieldId: { type: String, required: true },
      uploadText: String,
      maxFileSize: { type: Number, default: 10 * 1024 * 1024 },
      uploadConfig: {
        type: Object,
        default: () => ({ type: 'form_file', context: 'form' }),
      },
      formId: { type: String, required: true },
      // When true, the saved drawing is intended for project.situation_photos
      isSituationPhoto: { type: Boolean, default: false },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const fileInput = ref(null);
      const canvas = ref(null);
      const canvasWrapper = ref(null);

      // 'choose' = pick photo source, 'edit' = drawing canvas
      const step = ref('choose');

      const isUploading = ref(false);
      const uploadError = ref('');
      const fileUploadService = ref(null);

      const currentImage = ref(null); // HTMLImageElement
      const currentColor = ref(DEFAULT_COLORS[0]);
      const brushSize = ref(6);

      // 'pen' (default freehand drawing) or 'text' (tap to place/edit text)
      const tool = ref('pen');

      // Drawn strokes. Pen stroke:  { type: 'pen',  color, size, points: [{x,y}] }
      //                Text stroke: { type: 'text', color, fontSize, text, x, y, width, height }
      // Strokes without `type` are treated as 'pen' for backwards compatibility.
      const strokes = ref([]);
      const activeStroke = ref(null);

      // Text editor overlay state (for adding / editing a text stroke).
      // The stroke at `index` is mutated in real-time while the editor is
      // open (live preview); `backup` lets us restore on Cancel in edit mode.
      const textEditor = ref({
        open: false,
        mode: 'new', // 'new' | 'edit'
        value: '',
        index: null,
        anchorX: 0,
        anchorY: 0,
      });
      const textEditorBackup = ref(null);

      // Saved photos (always treated as a list; single mode uses index 0)
      const savedPhotos = ref([]);

      // ── Fullscreen + zoom/pan state ─────────────────────────
      const isFullscreen = ref(false);
      const zoom = ref(1);
      const panX = ref(0);
      const panY = ref(0);

      // Active pointer tracking for gestures (Map<pointerId, {x, y}>)
      const activePointers = new Map();
      // 'idle' | 'drawing' | 'gesture'
      const gestureMode = ref('idle');
      // Baseline values captured at start of a pinch gesture, used to compute
      // delta scale/pan during pointer movement.
      let pinchBaseline = null;

      const canvasTransformStyle = computed(() => ({
        transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
        transformOrigin: '0 0',
      }));

      const savedPhoto = computed(() => savedPhotos.value[0] || null);
      const canUndo = computed(() => strokes.value.length > 0);

      const availableColors = DEFAULT_COLORS;

      onMounted(async () => {
        fileUploadService.value = await getFileUploadService();
      });

      // ── Photo source selection ──────────────────────────────
      // We rely on the native OS picker (iOS / Android) which already shows
      // "Take Photo / Photo Library / Browse" when accept="image/*" is set
      // without a `capture` attribute — no custom bottom sheet needed.
      const isMobileDevice = () => {
        if (typeof window === 'undefined') return false;
        try {
          const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
          const small = window.matchMedia?.('(max-width: 1024px)')?.matches;
          return !!(coarse && small);
        } catch (_) {
          return /Mobi|Android|iPhone|iPad|iPod/i.test(
            navigator?.userAgent || ''
          );
        }
      };

      const openFilePicker = () => {
        uploadError.value = '';
        fileInput.value?.click();
      };

      const onSourcePicked = async (event) => {
        const file = event.target.files?.[0];
        // Reset so the same file can be re-selected
        event.target.value = '';
        if (!file) return;

        if (file.size > props.maxFileSize) {
          uploadError.value = `Bestand te groot (max ${Math.round(
            props.maxFileSize / 1024 / 1024
          )} MB)`;
          return;
        }

        // Use FileReader → data: URL instead of blob: URL so we don't depend on
        // the host CSP allowing blob: in img-src. Data URLs are already allowed.
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
            reader.readAsDataURL(file);
          });

          const img = new Image();
          img.onload = async () => {
            currentImage.value = img;
            strokes.value = [];
            activeStroke.value = null;
            resetView();
            // Auto-enter fullscreen on mobile so there's room to draw + zoom
            isFullscreen.value = isMobileDevice();
            step.value = 'edit';
            await nextTick();
            drawScene();
          };
          img.onerror = () => {
            uploadError.value = 'Kon de foto niet laden';
          };
          img.src = dataUrl;
        } catch (err) {
          console.error('Failed to read photo:', err);
          uploadError.value = 'Kon de foto niet inlezen';
        }
      };

      // ── Canvas drawing ──────────────────────────────────────
      const setupCanvasSize = () => {
        if (!canvas.value || !currentImage.value) return;
        const img = currentImage.value;

        // Scale image down if too large but preserve aspect ratio
        let { width, height } = img;
        const max = Math.max(width, height);
        if (max > MAX_CANVAS_DIMENSION) {
          const scale = MAX_CANVAS_DIMENSION / max;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        canvas.value.width = width;
        canvas.value.height = height;
      };

      // Brightness heuristic so text gets a contrasting outline regardless
      // of the chosen text color (white text → black outline, etc.).
      const isLightColor = (hex) => {
        if (!hex || typeof hex !== 'string') return false;
        const m = hex.replace('#', '');
        if (m.length < 6) return false;
        const r = parseInt(m.slice(0, 2), 16);
        const g = parseInt(m.slice(2, 4), 16);
        const b = parseInt(m.slice(4, 6), 16);
        // Perceptual luminance
        return 0.299 * r + 0.587 * g + 0.114 * b > 160;
      };

      // Convert brush slider (2..20) to a text font-size in canvas pixels.
      const fontSizeForBrush = (size) => Math.round(size * 5) + 8;

      const drawTextStroke = (ctx, stroke) => {
        const fontSize = stroke.fontSize || fontSizeForBrush(brushSize.value);
        ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.textBaseline = 'top';
        ctx.lineJoin = 'round';
        ctx.lineWidth = Math.max(3, fontSize / 6);
        ctx.strokeStyle = isLightColor(stroke.color) ? '#000' : '#fff';
        ctx.strokeText(stroke.text, stroke.x, stroke.y);
        ctx.fillStyle = stroke.color;
        ctx.fillText(stroke.text, stroke.x, stroke.y);

        // Cache bounding box for hit-testing & drag selection. Use textBaseline=top
        // so y is the top-left of the glyph box (height ≈ fontSize * 1.2).
        const measure = ctx.measureText(stroke.text);
        stroke.width = measure.width;
        stroke.height = fontSize * 1.2;
      };

      const drawScene = () => {
        if (!canvas.value || !currentImage.value) return;
        setupCanvasSize();
        const ctx = canvas.value.getContext('2d');
        ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
        ctx.drawImage(currentImage.value, 0, 0, canvas.value.width, canvas.value.height);

        const allStrokes = activeStroke.value
          ? [...strokes.value, activeStroke.value]
          : strokes.value;

        for (const stroke of allStrokes) {
          if (stroke.type === 'text') {
            if (!stroke.text) continue;
            drawTextStroke(ctx, stroke);
            continue;
          }

          // Pen stroke (also default for legacy strokes without `type`)
          if (!stroke.points || !stroke.points.length) continue;
          ctx.strokeStyle = stroke.color;
          ctx.lineWidth = stroke.size;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
          for (let i = 1; i < stroke.points.length; i++) {
            ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
          }
          ctx.stroke();
        }
      };

      // getBoundingClientRect already accounts for CSS transforms (zoom/pan),
      // so screen → canvas coords automatically compensate for the current zoom level.
      const getCanvasCoords = (event) => {
        if (!canvas.value) return null;
        const rect = canvas.value.getBoundingClientRect();
        const scaleX = canvas.value.width / rect.width;
        const scaleY = canvas.value.height / rect.height;
        return {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY,
        };
      };

      // ── Gesture helpers ─────────────────────────────────────
      const distanceBetween = (p1, p2) => {
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        return Math.sqrt(dx * dx + dy * dy);
      };

      const midpointBetween = (p1, p2) => ({
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      });

      const getWrapperRect = () => canvasWrapper.value?.getBoundingClientRect();

      const startPinch = () => {
        const rect = getWrapperRect();
        if (!rect) return;
        const pts = Array.from(activePointers.values()).slice(0, 2);
        if (pts.length < 2) return;
        // Pointer coords relative to the wrapper (not the scaled canvas)
        const local = pts.map((p) => ({ x: p.x - rect.left, y: p.y - rect.top }));
        pinchBaseline = {
          startDistance: distanceBetween(local[0], local[1]),
          startMidpoint: midpointBetween(local[0], local[1]),
          startZoom: zoom.value,
          startPanX: panX.value,
          startPanY: panY.value,
        };
      };

      const applyPinch = () => {
        const rect = getWrapperRect();
        if (!rect || !pinchBaseline) return;
        const pts = Array.from(activePointers.values()).slice(0, 2);
        if (pts.length < 2) return;
        const local = pts.map((p) => ({ x: p.x - rect.left, y: p.y - rect.top }));

        const currentDistance = distanceBetween(local[0], local[1]);
        const currentMidpoint = midpointBetween(local[0], local[1]);

        const scaleRatio = currentDistance / (pinchBaseline.startDistance || 1);
        let newZoom = pinchBaseline.startZoom * scaleRatio;
        newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

        // Keep the canvas point under the original midpoint anchored at the new
        // midpoint while combining pinch scaling and translation by midpoint delta.
        // Canvas point under start midpoint (in canvas-local coords pre-transform):
        //   cp = (m0 - pan0) / scale0
        // After transform we want: m = newPan + cp * newZoom
        //   → newPan = m - cp * newZoom
        const cpx = (pinchBaseline.startMidpoint.x - pinchBaseline.startPanX) / pinchBaseline.startZoom;
        const cpy = (pinchBaseline.startMidpoint.y - pinchBaseline.startPanY) / pinchBaseline.startZoom;

        zoom.value = newZoom;
        panX.value = currentMidpoint.x - cpx * newZoom;
        panY.value = currentMidpoint.y - cpy * newZoom;
      };

      const resetView = () => {
        zoom.value = 1;
        panX.value = 0;
        panY.value = 0;
      };

      // ── Hit-testing for text strokes ────────────────────────
      // Iterate top-most first (last drawn = on top). A small touch padding
      // makes finger-targeting more forgiving.
      const findTextStrokeAt = (x, y, padding = 6) => {
        for (let i = strokes.value.length - 1; i >= 0; i--) {
          const s = strokes.value[i];
          if (s.type !== 'text' || !s.width || !s.height) continue;
          if (
            x >= s.x - padding &&
            x <= s.x + s.width + padding &&
            y >= s.y - padding &&
            y <= s.y + s.height + padding
          ) {
            return i;
          }
        }
        return null;
      };

      // Movement (in canvas pixels) before a press becomes a drag in text mode.
      const TEXT_DRAG_THRESHOLD = 6;

      // State for text-mode interaction. Distinct from gestureMode so it can't
      // collide with pinch detection (pinch still wins as soon as a 2nd finger lands).
      // 'idle' | 'text-press' | 'text-drag'
      const textInteraction = ref({
        state: 'idle',
        index: null,        // stroke index being pressed/dragged
        startCanvas: null,  // initial canvas coord of pointer
        offset: null,       // offset from stroke origin to pointer when drag started
        pointerId: null,
      });

      // ── Pointer event handlers ──────────────────────────────
      const onPointerDown = (event) => {
        if (!canvas.value) return;
        // Don't intercept canvas events while the text editor overlay is open
        if (textEditor.value.open) return;
        try { canvas.value.setPointerCapture?.(event.pointerId); } catch (_) {}

        activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (activePointers.size >= 2) {
          // Switch to gesture mode: cancel any in-progress stroke or text drag
          if (gestureMode.value === 'drawing' && activeStroke.value) {
            activeStroke.value = null;
            drawScene();
          }
          if (textInteraction.value.state !== 'idle') {
            textInteraction.value = {
              state: 'idle', index: null, startCanvas: null, offset: null, pointerId: null,
            };
            drawScene();
          }
          gestureMode.value = 'gesture';
          startPinch();
          return;
        }

        // Single pointer → only start a new interaction if we're truly idle
        if (gestureMode.value !== 'idle') return;

        const point = getCanvasCoords(event);
        if (!point) return;

        if (tool.value === 'text') {
          const hitIndex = findTextStrokeAt(point.x, point.y);
          textInteraction.value = {
            state: 'text-press',
            index: hitIndex,
            startCanvas: point,
            offset: hitIndex !== null
              ? { x: point.x - strokes.value[hitIndex].x, y: point.y - strokes.value[hitIndex].y }
              : null,
            pointerId: event.pointerId,
          };
          return;
        }

        // Pen mode (default)
        gestureMode.value = 'drawing';
        activeStroke.value = {
          type: 'pen',
          color: currentColor.value,
          size: brushSize.value,
          points: [point],
        };
        drawScene();
      };

      const onPointerMove = (event) => {
        if (!activePointers.has(event.pointerId)) return;
        activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (gestureMode.value === 'gesture') {
          if (activePointers.size >= 2) {
            applyPinch();
          }
          // With only 1 active pointer left during gesture mode → ignore movement
          // (we wait until all fingers lift before going back to idle)
          return;
        }

        // Text-mode press/drag handling
        if (
          textInteraction.value.state !== 'idle' &&
          textInteraction.value.pointerId === event.pointerId
        ) {
          const point = getCanvasCoords(event);
          if (!point) return;
          const ti = textInteraction.value;

          if (ti.state === 'text-press') {
            // Promote to drag once we cross the movement threshold (only when
            // we actually have a stroke under the finger to drag).
            if (
              ti.index !== null &&
              ti.startCanvas &&
              distanceBetween(point, ti.startCanvas) > TEXT_DRAG_THRESHOLD
            ) {
              textInteraction.value = { ...ti, state: 'text-drag' };
            } else {
              return;
            }
          }

          if (textInteraction.value.state === 'text-drag' && textInteraction.value.index !== null) {
            const stroke = strokes.value[textInteraction.value.index];
            if (stroke) {
              stroke.x = point.x - (textInteraction.value.offset?.x || 0);
              stroke.y = point.y - (textInteraction.value.offset?.y || 0);
              drawScene();
            }
          }
          return;
        }

        if (gestureMode.value === 'drawing' && activeStroke.value) {
          const point = getCanvasCoords(event);
          if (!point) return;
          activeStroke.value.points.push(point);
          drawScene();
        }
      };

      const onPointerUp = (event) => {
        try { canvas.value?.releasePointerCapture?.(event.pointerId); } catch (_) {}
        activePointers.delete(event.pointerId);

        // Pen mode: finalize the in-progress stroke
        if (gestureMode.value === 'drawing') {
          if (activeStroke.value) {
            if (activeStroke.value.points.length > 1) {
              strokes.value.push(activeStroke.value);
            }
            activeStroke.value = null;
            drawScene();
          }
          if (activePointers.size === 0) gestureMode.value = 'idle';
          return;
        }

        // Text mode: distinguish tap vs drag-end
        if (
          textInteraction.value.state !== 'idle' &&
          textInteraction.value.pointerId === event.pointerId
        ) {
          const ti = textInteraction.value;
          if (ti.state === 'text-press') {
            // No drag → it was a tap
            if (ti.index !== null) {
              openTextEditorForExisting(ti.index);
            } else if (ti.startCanvas) {
              openTextEditorForNew(ti.startCanvas.x, ti.startCanvas.y);
            }
          }
          // text-drag → already committed positions live; nothing more to do
          textInteraction.value = {
            state: 'idle', index: null, startCanvas: null, offset: null, pointerId: null,
          };
          return;
        }

        if (gestureMode.value === 'gesture') {
          if (activePointers.size === 0) {
            gestureMode.value = 'idle';
            pinchBaseline = null;
          } else if (activePointers.size >= 2) {
            // Re-baseline so remaining fingers don't cause a sudden jump
            startPinch();
          }
        }
      };

      const undo = () => {
        if (!strokes.value.length) return;
        strokes.value.pop();
        drawScene();
      };

      const clearStrokes = () => {
        strokes.value = [];
        drawScene();
      };

      const selectColor = (color) => {
        currentColor.value = color;
      };

      const selectTool = (next) => {
        tool.value = next;
        // Cancel anything in flight when switching tools
        if (activeStroke.value) {
          activeStroke.value = null;
          drawScene();
        }
        textInteraction.value = {
          state: 'idle', index: null, startCanvas: null, offset: null, pointerId: null,
        };
      };

      // ── Text editor ─────────────────────────────────────────
      const textInputRef = ref(null);

      // Reverse of fontSizeForBrush so that opening an existing text stroke
      // sets the slider to the value that produces its current font-size.
      const brushForFontSize = (fs) => {
        const candidate = Math.round(((fs || 0) - 8) / 5);
        return Math.max(2, Math.min(20, candidate));
      };

      const closeTextEditor = () => {
        textEditor.value = {
          open: false, mode: 'new', value: '', index: null, anchorX: 0, anchorY: 0,
        };
        textEditorBackup.value = null;
      };

      const openTextEditorForNew = (canvasX, canvasY) => {
        // Push an empty placeholder stroke immediately. The live-preview watcher
        // will mutate it as the user types / drags the size slider / picks a color.
        // Empty-text strokes render as nothing in drawScene().
        strokes.value.push({
          type: 'text',
          text: '',
          x: canvasX,
          y: canvasY,
          color: currentColor.value,
          fontSize: fontSizeForBrush(brushSize.value),
        });
        textEditorBackup.value = null;
        textEditor.value = {
          open: true,
          mode: 'new',
          value: '',
          index: strokes.value.length - 1,
          anchorX: canvasX,
          anchorY: canvasY,
        };
        nextTick(() => textInputRef.value?.focus?.());
      };

      const openTextEditorForExisting = (index) => {
        const stroke = strokes.value[index];
        if (!stroke || stroke.type !== 'text') return;
        // Snapshot so we can restore on Cancel
        textEditorBackup.value = { ...stroke };
        // Sync toolbar to the stroke being edited so live changes feel natural
        currentColor.value = stroke.color;
        brushSize.value = brushForFontSize(stroke.fontSize);
        textEditor.value = {
          open: true,
          mode: 'edit',
          value: stroke.text,
          index,
          anchorX: stroke.x,
          anchorY: stroke.y,
        };
        nextTick(() => {
          textInputRef.value?.focus?.();
          textInputRef.value?.select?.();
        });
      };

      const commitTextEditor = () => {
        const trimmed = (textEditor.value.value || '').trim();
        const { index } = textEditor.value;
        if (index !== null) {
          const stroke = strokes.value[index];
          if (stroke) {
            if (!trimmed) {
              // Empty after typing → drop the stroke instead of saving an invisible blob
              strokes.value.splice(index, 1);
            } else {
              stroke.text = trimmed;
            }
          }
        }
        closeTextEditor();
        drawScene();
      };

      const cancelTextEditor = () => {
        const { mode, index } = textEditor.value;
        if (index !== null) {
          if (mode === 'new') {
            // Discard the placeholder we created when opening
            strokes.value.splice(index, 1);
          } else if (textEditorBackup.value) {
            // Restore the original stroke values
            strokes.value[index] = textEditorBackup.value;
          }
        }
        closeTextEditor();
        drawScene();
      };

      const deleteTextEditor = () => {
        const { index } = textEditor.value;
        if (index !== null) {
          strokes.value.splice(index, 1);
        }
        closeTextEditor();
        drawScene();
      };

      // Live-preview: while the text editor is open, mirror typing + slider +
      // color changes onto the stroke at textEditor.index, then redraw.
      watch(
        [
          () => textEditor.value.open,
          () => textEditor.value.value,
          () => brushSize.value,
          () => currentColor.value,
        ],
        () => {
          if (!textEditor.value.open || textEditor.value.index === null) return;
          const stroke = strokes.value[textEditor.value.index];
          if (!stroke || stroke.type !== 'text') return;
          stroke.text = textEditor.value.value || '';
          stroke.color = currentColor.value;
          stroke.fontSize = fontSizeForBrush(brushSize.value);
          drawScene();
        }
      );

      // ── Fullscreen ──────────────────────────────────────────
      const toggleFullscreen = () => {
        isFullscreen.value = !isFullscreen.value;
        // Reset zoom/pan when toggling so the canvas re-centers cleanly
        resetView();
      };

      // ── Save / cancel ───────────────────────────────────────
      const cancelEdit = () => {
        currentImage.value = null;
        strokes.value = [];
        resetView();
        isFullscreen.value = false;
        gestureMode.value = 'idle';
        activePointers.clear();
        pinchBaseline = null;
        tool.value = 'pen';
        textInteraction.value = {
          state: 'idle', index: null, startCanvas: null, offset: null, pointerId: null,
        };
        closeTextEditor();
        step.value = 'choose';
      };

      const resetCurrent = () => {
        cancelEdit();
      };

      const blobFromCanvas = () =>
        new Promise((resolve, reject) => {
          if (!canvas.value) {
            reject(new Error('Canvas niet beschikbaar'));
            return;
          }
          canvas.value.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Kon afbeelding niet exporteren'));
            },
            'image/jpeg',
            0.9
          );
        });

      const saveDrawing = async () => {
        if (!fileUploadService.value || !canvas.value) return;
        uploadError.value = '';
        isUploading.value = true;
        try {
          const blob = await blobFromCanvas();
          const file = new File(
            [blob],
            `photo-drawing-${Date.now()}.jpg`,
            { type: 'image/jpeg' }
          );
          const response = await fileUploadService.value.uploadFile(file, {
            ...props.uploadConfig,
            formId: props.formId,
            fieldId: props.fieldId,
            kind: 'photo_drawing',
            isSituationPhoto: props.isSituationPhoto || undefined,
          });

          if (!response?.url) {
            throw new Error('Upload mislukt: geen URL ontvangen');
          }

          if (props.multiple) {
            savedPhotos.value = [...savedPhotos.value, { url: response.url }];
            emit(
              'update:modelValue',
              savedPhotos.value.map((p) => p.url)
            );
          } else {
            savedPhotos.value = [{ url: response.url }];
            emit('update:modelValue', response.url);
          }

          step.value = 'choose';
          currentImage.value = null;
          strokes.value = [];
          resetView();
          isFullscreen.value = false;
          gestureMode.value = 'idle';
          activePointers.clear();
          pinchBaseline = null;
          tool.value = 'pen';
          textInteraction.value = {
            state: 'idle', index: null, startCanvas: null, offset: null, pointerId: null,
          };
          closeTextEditor();
        } catch (err) {
          console.error('Photo drawing upload failed:', err);
          uploadError.value = err?.message || 'Upload mislukt. Probeer opnieuw.';
        } finally {
          isUploading.value = false;
        }
      };

      const removeSaved = (idx) => {
        savedPhotos.value = savedPhotos.value.filter((_, i) => i !== idx);
        if (props.multiple) {
          emit('update:modelValue', savedPhotos.value.map((p) => p.url));
        } else {
          emit('update:modelValue', '');
        }
      };

      // Sync inbound modelValue
      watch(
        () => props.modelValue,
        (newValue) => {
          if (props.multiple) {
            const urls = Array.isArray(newValue)
              ? newValue
              : newValue
                ? [newValue]
                : [];
            savedPhotos.value = urls.filter(Boolean).map((url) => ({ url }));
          } else {
            savedPhotos.value = newValue ? [{ url: newValue }] : [];
          }
        },
        { immediate: true }
      );

      // Redraw on resize so canvas keeps proportions
      const handleResize = () => {
        if (step.value === 'edit') drawScene();
      };
      onMounted(() => window.addEventListener('resize', handleResize));
      onBeforeUnmount(() => window.removeEventListener('resize', handleResize));

      // Lock background scroll while the fullscreen editor is open
      watch(isFullscreen, (fullscreen) => {
        if (typeof document === 'undefined') return;
        if (fullscreen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      });
      onBeforeUnmount(() => {
        if (typeof document !== 'undefined') document.body.style.overflow = '';
      });

      // Auto-save in-progress drawing when the parent form navigates / submits.
      // FormView provides a Set of flush callbacks that it awaits before
      // moving on, so the user doesn't lose pending strokes by clicking
      // "Volgende" / "Toevoegen aan offerte" without first hitting "Opslaan".
      const pendingFlushers = inject('formPendingFlushers', null);
      const flushPendingDrawing = async () => {
        if (step.value !== 'edit' || !currentImage.value || isUploading.value) return;
        await saveDrawing();
      };
      watch(
        [step, currentImage],
        () => {
          if (!pendingFlushers) return;
          if (step.value === 'edit' && currentImage.value) {
            pendingFlushers.add(flushPendingDrawing);
          } else {
            pendingFlushers.delete(flushPendingDrawing);
          }
        },
        { immediate: true }
      );
      onBeforeUnmount(() => {
        if (pendingFlushers) pendingFlushers.delete(flushPendingDrawing);
      });

      return {
        fileInput,
        canvas,
        canvasWrapper,
        textInputRef,
        step,
        isUploading,
        uploadError,
        currentColor,
        brushSize,
        strokes,
        canUndo,
        availableColors,
        savedPhotos,
        savedPhoto,
        // Fullscreen + zoom
        isFullscreen,
        zoom,
        panX,
        panY,
        gestureMode,
        canvasTransformStyle,
        toggleFullscreen,
        resetView,
        // Tool + text editor
        tool,
        textEditor,
        selectTool,
        commitTextEditor,
        cancelTextEditor,
        deleteTextEditor,
        closeTextEditor,
        // Handlers
        openFilePicker,
        onSourcePicked,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        undo,
        clearStrokes,
        selectColor,
        cancelEdit,
        resetCurrent,
        saveDrawing,
        removeSaved,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sf-photo-drawing {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    &__instruction {
      margin: 0;
      font-size: 0.875rem;
      color: var(--cs-text-body, #64748b);
    }

    // Single dropzone CTA
    &__dropzone {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      padding: 1.5rem 1rem;
      border: 2px dashed var(--cs-input-default-border, #e2e8f0);
      border-radius: 0.5rem;
      background: var(--cs-input-default-background, #fff);
      cursor: pointer;
      transition: border-color 0.15s ease, background 0.15s ease;
      text-align: center;

      &:hover {
        border-color: var(--cs-brand-primary, #3b82f6);
        background: rgba(59, 130, 246, 0.04);
      }
    }

    &__dropzone-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.625rem;
      border: 1px solid var(--cs-input-default-border, #e2e8f0);
      border-radius: 0.5rem;
      color: var(--cs-text-body, #64748b);
    }

    &__dropzone-text {
      margin: 0.25rem 0 0;
      font-weight: 500;
      font-size: 0.9375rem;
      color: var(--cs-input-default-text, #0f172a);
    }

    &__dropzone-hint {
      margin: 0;
      font-size: 0.75rem;
      color: var(--cs-text-body, #64748b);
    }

    // Editor canvas
    &__editor {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      padding: 0.625rem;
      border: 1px solid var(--cs-input-default-border, #e2e8f0);
      border-radius: 0.5rem;
      background: var(--cs-input-default-background, #fff);

      &--fullscreen {
        position: fixed;
        inset: 0;
        z-index: 9998;
        border-radius: 0;
        border: none;
        padding: 0.5rem;
        background: #0f172a;
        gap: 0.5rem;

        // Use safe-area inset so we don't sit under the iOS notch/home bar
        padding-top: max(0.5rem, env(safe-area-inset-top));
        padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
        padding-left: max(0.5rem, env(safe-area-inset-left));
        padding-right: max(0.5rem, env(safe-area-inset-right));

        .sf-photo-drawing__toolbar {
          background: rgba(255, 255, 255, 0.08);
          color: white;
        }

        .sf-photo-drawing__brush-label {
          color: rgba(255, 255, 255, 0.7);
        }

        .sf-photo-drawing__canvas-wrapper {
          flex: 1;
          min-height: 0;
          max-height: none;
          background: #0f172a;
        }

        .sf-photo-drawing__canvas {
          max-width: 100%;
          max-height: 100%;
        }

        .sf-photo-drawing__editor-actions {
          justify-content: space-between;
        }
      }
    }

    &__toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.75rem;
      padding: 0.5rem;
      background: var(--cs-background-primary, #f8fafc);
      border-radius: 0.375rem;
    }

    &__tool-toggle {
      display: inline-flex;
      gap: 0.25rem;
      padding: 0.25rem;
      background: rgba(15, 23, 42, 0.05);
      border-radius: 0.5rem;
    }

    &__tool-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.625rem;
      border: 1px solid transparent;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--cs-text-body, #64748b);
      font-size: 0.8125rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

      &:hover:not(.sf-photo-drawing__tool-btn--active) {
        background: rgba(15, 23, 42, 0.04);
        color: var(--cs-text-heading, #0f172a);
      }

      &--active {
        background: white;
        border-color: var(--cs-input-default-border, #e2e8f0);
        color: var(--cs-brand-primary, #3b82f6);
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
      }
    }

    &__tool-letter {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      font-size: 12px;
      font-weight: 800;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      line-height: 1;
    }

    &__colors {
      display: flex;
      gap: 0.375rem;
      flex-wrap: wrap;
    }

    &__color {
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 50%;
      border: 2px solid rgba(0, 0, 0, 0.1);
      cursor: pointer;
      padding: 0;
      transition: transform 0.1s ease, box-shadow 0.15s ease;

      &:hover {
        transform: scale(1.1);
      }

      &--active {
        border-color: var(--cs-brand-primary, #3b82f6);
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
      }
    }

    &__brush {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding-left: 0.625rem;
      border-left: 1px solid var(--cs-input-default-border, #e2e8f0);
    }

    &__brush-label {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--cs-text-body, #64748b);
    }

    &__brush-range {
      width: 6.25rem;
      accent-color: var(--cs-brand-primary, #3b82f6);
    }

    &__tools {
      display: flex;
      gap: 0.375rem;
      margin-left: auto;
    }

    &__canvas-wrapper {
      position: relative;
      width: 100%;
      max-height: 60vh;
      overflow: hidden;
      border-radius: 0.375rem;
      background: #000;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__canvas {
      max-width: 100%;
      max-height: 60vh;
      display: block;
      touch-action: none;
      cursor: crosshair;
      // CSS transform comes via inline :style binding (translate + scale)
      will-change: transform;

      &--text-mode {
        cursor: text;
      }
    }

    &__text-editor {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 5;
      padding: 0.5rem;
      background: linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.85) 60%);
      animation: photo-drawing-fade-in 0.15s ease;
    }

    &__text-editor-bar {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.625rem;
      background: white;
      border-radius: 0.5rem;
      box-shadow: 0 4px 16px rgba(15, 23, 42, 0.18);
    }

    &__text-editor-title {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--cs-text-body, #64748b);
    }

    &__text-editor-input {
      width: 100%;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--cs-input-default-border, #e2e8f0);
      border-radius: 0.375rem;
      font-size: 1rem;
      font-weight: 600;
      background: white;
      // Color is set inline via :style so the user sees the chosen text color while typing

      &:focus {
        outline: none;
        border-color: var(--cs-brand-primary, #3b82f6);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
      }
    }

    &__text-editor-actions {
      display: flex;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: 0.375rem;
    }

    &__gesture-hint {
      position: absolute;
      top: 0.5rem;
      left: 50%;
      transform: translateX(-50%);
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.75rem;
      background: rgba(15, 23, 42, 0.85);
      color: white;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 500;
      pointer-events: none;
      animation: photo-drawing-fade-in 0.15s ease;
    }

    &__zoom-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--cs-text-body, #64748b);
      min-width: 2.5rem;
      text-align: center;
    }

    &__editor-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }

    // Buttons (shared)
    &__btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      padding: 0.5rem 0.875rem;
      border: 1px solid var(--cs-input-default-border, #e2e8f0);
      border-radius: 0.375rem;
      background: var(--cs-input-default-background, #fff);
      color: var(--cs-input-default-text, #0f172a);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease;

      &:hover:not(:disabled) {
        background: var(--cs-background-primary, #f8fafc);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &--primary {
        background: var(--cs-brand-primary, #3b82f6);
        border-color: var(--cs-brand-primary, #3b82f6);
        color: white;

        &:hover:not(:disabled) {
          background: var(--cs-brand-primary, #3b82f6);
          opacity: 0.92;
        }
      }

      &--danger {
        color: var(--red-600, #dc2626);
        border-color: var(--red-300, #fca5a5);

        &:hover:not(:disabled) {
          background: var(--red-50, #fef2f2);
        }
      }

      &--icon {
        padding: 0.5rem;
      }
    }

    // Saved single preview
    &__single-preview {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.625rem;
      border: 1px solid var(--cs-input-default-border, #e2e8f0);
      border-radius: 0.5rem;
      background: var(--cs-input-default-background, #fff);

      img {
        width: 100%;
        max-height: 280px;
        object-fit: contain;
        border-radius: 0.375rem;
        background: var(--cs-background-primary, #f8fafc);
      }
    }

    &__single-actions {
      display: flex;
      gap: 0.5rem;
      justify-content: center;
    }

    // Saved gallery (multiple mode)
    &__gallery {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
      gap: 0.5rem;
    }

    &__gallery-item {
      position: relative;
      aspect-ratio: 1;
      border-radius: 0.375rem;
      overflow: hidden;
      border: 1px solid var(--cs-input-default-border, #e2e8f0);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__gallery-remove {
      position: absolute;
      top: 0.25rem;
      right: 0.25rem;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 9999px;
      background: rgba(0, 0, 0, 0.55);
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease;

      &:hover {
        background: rgba(220, 38, 38, 0.85);
      }
    }

    &__error {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      background: var(--red-50, #fef2f2);
      border: 1px solid var(--red-200, #fecaca);
      border-radius: 0.375rem;
      color: var(--red-700, #b91c1c);
      font-size: 0.875rem;
    }

    &__spinner {
      width: 0.875rem;
      height: 0.875rem;
      border: 2px solid rgba(255, 255, 255, 0.4);
      border-top-color: white;
      border-radius: 50%;
      animation: photo-drawing-spin 0.8s linear infinite;
    }
  }

  @keyframes photo-drawing-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes photo-drawing-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
</style>
